import { useState, useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';
import { StickyBackBar } from '@/components/layout/StickyBackBar';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { PageLoadingState } from '@/components/ui/PageLoadingState';
import { RefetchIndicator } from '@/components/ui/RefetchIndicator';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useCreditTransactions, CreditTransaction } from '@/hooks/useCredits';
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    ClockIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';

const TRANSACTION_TYPE_OPTIONS = [
    { value: '', label: 'All Types' },
    { value: 'purchase', label: 'Purchase' },
    { value: 'usage', label: 'Usage' },
    { value: 'refund', label: 'Refund' },
    { value: 'manual_add', label: 'Manual Add' },
    { value: 'manual_deduct', label: 'Manual Deduct' },
];

function RefundPercentageBadge({ percentage }: { percentage: number }) {
    const isFullRefund = percentage === 100;
    const badgeClass = isFullRefund
        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
        : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';

    return (
        <span
            className={`ml-1 rounded-sm px-1.5 py-0.5 text-xs font-medium ${badgeClass}`}
            data-testid="refund-percentage-badge"
        >
            ({percentage}%)
        </span>
    );
}

function TransactionRow({ transaction }: { transaction: CreditTransaction }) {
    const isCredit = ['purchase', 'manual_add', 'refund'].includes(transaction.type);
    const isRefund = transaction.type === 'refund';
    const date = new Date(transaction.createdAt);

    return (
        <tr className="border-b border-gray-200 dark:border-gray-700">
            <td className="px-4 py-3">
                <p className="font-medium text-gray-900 dark:text-white">
                    {transaction.description}
                    {isRefund && transaction.refundPercentage && (
                        <RefundPercentageBadge percentage={transaction.refundPercentage} />
                    )}
                </p>
            </td>
            <td className="px-4 py-3">
                <span className="rounded-sm bg-gray-100 px-2 py-1 text-xs font-medium dark:bg-gray-700">
                    {transaction.typeLabel}
                </span>
            </td>
            <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                {date.toLocaleDateString()} {date.toLocaleTimeString()}
            </td>
            <td className="px-4 py-3">
                <span className="rounded-sm bg-gray-100 px-2 py-1 text-xs font-medium dark:bg-gray-700">
                    {transaction.statusLabel}
                </span>
            </td>
            <td className="px-4 py-3 text-right">
                <span
                    className={`text-lg font-semibold ${
                        isCredit ? 'text-green-600' : 'text-red-600'
                    }`}
                >
                    {isCredit ? '+' : '-'}{transaction.amount}
                </span>
            </td>
        </tr>
    );
}

export function TransactionHistoryPage() {
    usePageTitle('Transaction History');

    const [page, setPage] = useState(1);
    const [typeFilter, setTypeFilter] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearch = useDebouncedValue(searchQuery, 300);
    const limit = 20;

    const { data, isLoading, isFetching } = useCreditTransactions(
        page,
        limit,
        typeFilter || undefined,
        debouncedSearch || undefined
    );

    // Reset to page 1 when filters change
    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, typeFilter]);

    const handleTypeChange = (value: string) => {
        setTypeFilter(value);
    };

    const hasActiveFilters = typeFilter !== '' || searchQuery !== '';

    const handleResetFilters = () => {
        setTypeFilter('');
        setSearchQuery('');
    };

    // Show full spinner only on initial load (no data yet)
    if (!data && isLoading) {
        return (
            <Layout>
                <StickyBackBar destinationLabel="Credits" backPath="/credits" />
                <PageLoadingState />
            </Layout>
        );
    }

    return (
        <Layout>
            <StickyBackBar destinationLabel="Credits" backPath="/credits" />
            <div className="space-y-6 pt-4">
                <PageHeader title="Transaction History" />

                {/* Filters */}
                <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
                    <div className="flex-1 sm:max-w-xs">
                        <Input
                            label="Search"
                            placeholder="Search transactions..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="w-full sm:w-48">
                        <Select
                            label="Filter by type"
                            value={typeFilter}
                            onChange={handleTypeChange}
                            options={TRANSACTION_TYPE_OPTIONS}
                        />
                    </div>
                    {hasActiveFilters && (
                        <button
                            type="button"
                            onClick={handleResetFilters}
                            title="Reset filters"
                            className="flex h-10 w-10 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:border-gray-700 dark:bg-secondary dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                        >
                            <XMarkIcon className="h-5 w-5" />
                        </button>
                    )}
                </div>

                {/* Transactions Table */}
                <div className="relative overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-secondary">
                    <RefetchIndicator show={isFetching && !isLoading} />
                    {data?.transactions && data.transactions.length > 0 ? (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[600px] text-left">
                                    <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                                        <tr>
                                            <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                                Description
                                            </th>
                                            <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                                Type
                                            </th>
                                            <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                                Date
                                            </th>
                                            <th className="px-4 py-3 font-semibold text-gray-700 dark:text-gray-300">
                                                Status
                                            </th>
                                            <th className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-300">
                                                Amount
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.transactions.map((transaction) => (
                                            <TransactionRow
                                                key={transaction.id}
                                                transaction={transaction}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            <div className="flex items-center justify-center gap-2 border-t border-gray-200 px-4 py-3 dark:border-gray-700">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => setPage(page - 1)}
                                    disabled={page === 1 || isFetching}
                                >
                                    <ChevronLeftIcon className="h-4 w-4" />
                                    Previous
                                </Button>
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                    Page {page} of {data.totalPages}
                                </span>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => setPage(page + 1)}
                                    disabled={page >= data.totalPages || isFetching}
                                >
                                    Next
                                    <ChevronRightIcon className="h-4 w-4" />
                                </Button>
                            </div>
                        </>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                            <ClockIcon className="mb-2 h-8 w-8" />
                            <p>No transactions found</p>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}
